import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Input,
  Result,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { SafetyCertificateOutlined, LockOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { apiErrorCode, apiErrorFields, apiErrorMessage } from '@/api/client';
import { getInfo, sendCode, verifyCode, getSchema, validate, submit, type LinkInfo } from '@/api/collect';
import {
  collectSubmitValues,
  conditionFrom,
  COLLECT_INITIAL_VALUES,
  mergeValues,
  shouldRefreshSchema,
  validateLocally,
  type FormSchema,
  type FormValues,
} from '@/domain/schemaForm';
import SchemaForm, { toErrorMap } from '@/components/SchemaForm';
import { clearSession, loadSession, saveSession } from '@/domain/collectSession';

const { Paragraph, Text, Title } = Typography;

const CURRENCY_PATH = 'beneficiary.bank_details.account_currency';
const RESEND_SECONDS = 60;

/**
 * 到期前多久开始提示。
 *
 * 🔴 这个提示是这次改动的重点：填写人原先只能在点「提交」的那一刻才知道自己超时了，
 * 一屏银行信息白填。提前 5 分钟告诉他，他还来得及先提交或者先去收一封新码。
 */
const EXPIRY_WARNING_MS = 5 * 60 * 1000;

type Stage = 'loading' | 'unavailable' | 'verify' | 'form' | 'done';

/**
 * 收款信息收集页。**这是给组织外部的人用的，主要在手机上打开。**
 *
 * 设计上的几条取舍：
 * - 单列、大控件、底部固定提交按钮 —— 手机上一屏能填、拇指够得到；
 * - 不做分步向导：动态表单的字段互相牵动（改币种会换掉整组银行字段），
 *   分步会让人在第 3 步改了选项、前两步的输入却已经不适用；
 * - 任何失败都要给出人话，对方不是我们的同事，看不懂「AIRWALLEX_VALIDATION_FAILED」；
 * - 🔴 **界面文案一律英文**：填写人绝大多数是境外收款人。服务端的中文错误在
 *   api/client.ts 里被映射成英文，这里不要直接透传 error.message。
 */
export default function Collect() {
  const { token = '' } = useParams<{ token: string }>();
  const { message } = App.useApp();

  const [stage, setStage] = useState<Stage>('loading');
  const [info, setInfo] = useState<LinkInfo | null>(null);
  const [fatal, setFatal] = useState<string>('');

  // 邮箱验证。收件地址由创建人绑定，这里只展示掩码，没有输入框。
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [codeSent, setCodeSent] = useState(false);
  const [collectToken, setCollectToken] = useState<string | null>(null);
  /** 令牌到期时刻（毫秒）。服务端给的，界面据此提前提示、到点当场请人重新验证。 */
  const [expiresAtMs, setExpiresAtMs] = useState<number | null>(null);
  /** 到期前的提醒是否已经亮起来。 */
  const [expiringSoon, setExpiringSoon] = useState(false);
  /**
   * 「刚才是超时被退回来的，填过的内容还在」。
   * 🔴 退回验证步骤时**不清 values / schema** —— 让人把一屏银行信息重填一遍是不可接受的。
   */
  const [resumedAfterExpiry, setResumedAfterExpiry] = useState(false);

  // 表单
  const [schema, setSchema] = useState<FormSchema | null>(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [values, setValues] = useState<FormValues>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  /**
   * 「这一次是我刚填完的」还是「我打开时就已经填过了」。
   * 🔴 不能用 `!submitting` 代替：setSubmitting(false) 在 finally 里与 setStage('done')
   * 被 React 批到同一次渲染，渲染时 submitting 已经是 false，致谢文案那条分支永远不可达。
   */
  const [justSubmitted, setJustSubmitted] = useState(false);
  const formTop = useRef<HTMLDivElement>(null);

  // 冷却倒计时。清理函数不能省：组件在倒计时途中卸载时定时器会继续 setState。
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  /** 验证通过（或从 sessionStorage 恢复）后，把会话装上。 */
  const startSession = useCallback(
    (ct: string, expiresAt: string, persist: boolean) => {
      setCollectToken(ct);
      setExpiresAtMs(Date.parse(expiresAt));
      setExpiringSoon(false);
      if (persist) saveSession(token, { collectToken: ct, expiresAt });
    },
    [token],
  );

  /**
   * 令牌失效：**当场**退回验证步骤。
   *
   * 🔴 已填的 `values` 和当前 `schema` 一概不动。重新验证完会用 keep=true 合并回去，
   * 填写人一个字都不用重打 —— 让人把一屏银行账号重填一遍，不如不做这个功能。
   */
  const expireSession = useCallback(() => {
    clearSession(token);
    setCollectToken(null);
    setExpiresAtMs(null);
    setExpiringSoon(false);
    setCodeSent(false);
    setCode('');
    setResumedAfterExpiry(true);
    setStage('verify');
  }, [token]);

  /**
   * 盯着令牌的到期时刻：先提醒，到点当场退回验证步骤。
   *
   * 🔴 这就是这次要修的那个问题 —— 原先什么都不盯，人填完一屏才在提交时被拦。
   *
   * ⚠️ `setTimeout` 在页面被挂起（手机锁屏、切后台）时不会按时醒，所以提交那一步
   * 仍然留着兜底分支，两者都要有。
   */
  useEffect(() => {
    if (expiresAtMs === null || stage !== 'form') return;
    const left = expiresAtMs - Date.now();
    if (left <= 0) {
      // 外部时钟已经到期，必须把会话状态同步回验证步骤。
      // oxlint-disable-next-line react/set-state-in-effect
      expireSession();
      return;
    }
    const timers = [window.setTimeout(expireSession, left)];
    if (left > EXPIRY_WARNING_MS) {
      timers.push(window.setTimeout(() => setExpiringSoon(true), left - EXPIRY_WARNING_MS));
    } else {
      // 这是把外部时钟同步进界面状态；到这里说明令牌已经进入提醒窗口。
      // oxlint-disable-next-line react/set-state-in-effect
      setExpiringSoon(true);
    }
    return () => timers.forEach(window.clearTimeout);
  }, [expiresAtMs, stage, expireSession]);

  /**
   * 拉 schema。与主站同一套「循环拉到收敛」的逻辑：新 schema 的 default 可能补全出新的
   * 条件字段（选完国家+币种后汇款方式才带着 default 出现），不再拉一轮字段就长不出来。
   */
  const refreshSchema = useCallback(
    async (current: FormValues, keep: boolean, ct: string | null) => {
      setSchemaLoading(true);
      try {
        // 不保留旧值时从预设起步，而不是空对象 —— 主体类型要预选「个人」
        // （见 domain/schemaForm.ts 的 COLLECT_INITIAL_VALUES）
        let acc: FormValues = keep ? current : { ...COLLECT_INITIAL_VALUES };
        let latest: FormSchema | null = null;
        for (let round = 0; round < 3; round++) {
          const sent = conditionFrom(acc);
          const next = await getSchema(token, ct, sent);
          latest = next;
          acc = mergeValues(next, acc);
          if (JSON.stringify(sent) === JSON.stringify(conditionFrom(acc))) break;
        }
        if (latest) {
          setSchema(latest);
          setValues(acc);
          setErrors({});
        }
      } catch (err) {
        // 改国家 / 币种时也会打这个接口，所以超时往往在这里先暴露 —— 早一步退回验证，
        // 比让人继续填一张永远提交不了的表好
        if (apiErrorCode(err) === 'VERIFICATION_REQUIRED') {
          expireSession();
          return;
        }
        message.error(apiErrorMessage(err));
      } finally {
        setSchemaLoading(false);
      }
    },
    [token, message, expireSession],
  );

  // 进页面先拿链接信息，决定走验证还是直接填
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getInfo(token);
        if (!alive) return;
        setInfo(res);
        if (res.status !== 'PENDING') {
          setStage('done');
          return;
        }
        if (res.requireEmailVerification) {
          // 刷新一下不该重新收一封验证码：服务端那个令牌多半还活着，
          // 之前只是被浏览器扔了（见 domain/collectSession.ts）
          const resumed = loadSession(token);
          if (resumed) {
            startSession(resumed.collectToken, resumed.expiresAt, false);
            setStage('form');
            void refreshSchema({}, false, resumed.collectToken);
          } else {
            setStage('verify');
          }
        } else {
          setStage('form');
          void refreshSchema({}, false, null);
        }
      } catch (err) {
        if (!alive) return;
        setFatal(apiErrorMessage(err, 'This link is invalid or has expired.'));
        setStage('unavailable');
      }
    })();
    return () => {
      alive = false;
    };
  }, [token, refreshSchema, startSession]);

  async function requestCode() {
    setSending(true);
    try {
      await sendCode(token);
      setCodeSent(true);
      setCooldown(RESEND_SECONDS);
      message.success('Verification code sent. Please check your inbox.');
    } catch (err) {
      message.error(apiErrorMessage(err));
    } finally {
      setSending(false);
    }
  }

  async function submitCode() {
    if (code.length !== 6) {
      message.warning('Enter the 6-digit code.');
      return;
    }
    setVerifying(true);
    try {
      const { collectToken: ct, expiresAt } = await verifyCode(token, code);
      startSession(ct, expiresAt, true);
      setStage('form');
      // 🔴 超时退回来的话用 keep=true：把刚才填的一并带回去，一个字都不用重打。
      // 第一次进来才从预设起步（keep=false）。
      const resuming = resumedAfterExpiry;
      setResumedAfterExpiry(false);
      void refreshSchema(values, resuming, ct);
    } catch (err) {
      message.error(apiErrorMessage(err));
      setCode('');
    } finally {
      setVerifying(false);
    }
  }

  /**
   * 🔴 判据是 shouldRefreshSchema，不是字段上的 `refresh` 标记 —— 后者也挂在邮编这类
   * 自由输入框上，照它执行的话邮编根本打不进去（见 domain/schemaForm.ts 的说明）。
   */
  function onFieldChange(path: string, value: string) {
    const next = { ...values, [path]: value };
    setValues(next);
    setErrors((e) => {
      const { [path]: _drop, ...rest } = e;
      return rest;
    });
    if (shouldRefreshSchema(values, next)) void refreshSchema(next, true, collectToken);
  }

  async function doSubmit() {
    if (!schema) return;
    const local = validateLocally(schema, values);
    if (local.length > 0) {
      setErrors(toErrorMap(local));
      message.warning('Please complete the highlighted fields.');
      // 手机上错误字段可能在屏幕外，滚回表单顶部
      formTop.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    const payload = collectSubmitValues(schema, values);
    setSubmitting(true);
    try {
      await validate(token, collectToken, payload);
      await submit(token, collectToken, payload);
      clearSession(token);
      setJustSubmitted(true);
      setStage('done');
      setInfo((i) => (i ? { ...i, status: 'FILLED' } : i));
    } catch (err) {
      const fields = apiErrorFields(err);
      if (fields.length > 0) {
        setErrors(
          toErrorMap(
            fields.map((path) => ({
              path,
              message: "This doesn't meet the bank's requirements.",
            })),
          ),
        );
        formTop.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      if (apiErrorCode(err) === 'VERIFICATION_REQUIRED') {
        // 兜底：正常情况下到期前的定时器已经先一步退回验证了，走到这里说明
        // 那个定时器没赶上（页面被挂起、系统休眠过）。values 照样保住。
        expireSession();
        return;
      }
      message.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  // ── 渲染 ──────────────────────────────────────────────────────────────────

  if (stage === 'loading') {
    return (
      <Centered>
        <Spin size="large" />
      </Centered>
    );
  }

  if (stage === 'unavailable') {
    return (
      <Centered>
        <Result status="404" title="Link unavailable" subTitle={fatal} />
      </Centered>
    );
  }

  if (stage === 'done') {
    return (
      <Centered>
        <Result
          status="success"
          title="Payment details submitted"
          subTitle={
            justSubmitted
              ? 'Thank you. These details will be used the next time you are paid.'
              : 'This link has already been completed. There is no need to submit again.'
          }
        />
      </Centered>
    );
  }

  const locked = info?.currency ? new Set([CURRENCY_PATH]) : undefined;

  return (
    <div style={{ minHeight: '100dvh', background: '#f5f5f5' }}>
      <div
        style={{
          maxWidth: 520,
          margin: '0 auto',
          // 安全区：刘海屏/底部横条不遮挡内容
          padding: 'max(16px, env(safe-area-inset-top)) 16px calc(88px + env(safe-area-inset-bottom))',
        }}
      >
        <Card styles={{ body: { padding: 20 } }}>
          <Title level={4} style={{ marginTop: 0, marginBottom: 4 }}>
            {info?.title}
          </Title>
          <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 16 }}>
            {info?.organizationName} has asked you to provide your payment details so they can pay
            you.
            {info?.currency && (
              <>
                {' '}
                The payment currency is fixed to <Tag color="blue">{info.currency}</Tag>
              </>
            )}
          </Paragraph>

          {stage === 'verify' && (
            <>
              <Alert
                type={resumedAfterExpiry ? 'warning' : 'info'}
                showIcon
                icon={<LockOutlined />}
                style={{ marginBottom: 16 }}
                title={
                  resumedAfterExpiry ? 'Please verify your email again' : 'Verify your email first'
                }
                description={
                  <>
                    {resumedAfterExpiry ? (
                      <>
                        {/* 🔴 必须明说内容还在。不说的话，人会以为白填了，
                            直接关掉页面 —— 而那些内容其实还在这个页面里。 */}
                        Your verification has expired for security reasons.{' '}
                        <Text strong>Everything you have already filled in is still here</Text> and
                        will come back as soon as you verify again. We will send a new code to{' '}
                        <Text strong>{info?.emailHint}</Text>.
                      </>
                    ) : (
                      <>
                        We will send a verification code to <Text strong>{info?.emailHint}</Text>.
                        Enter it below to confirm that it is really you submitting these details.
                        {/* 不显示完整地址：链接会被转发，回显完整邮箱等于把它一起散播出去 */}
                      </>
                    )}
                  </>
                }
              />
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {!codeSent ? (
                  <Button type="primary" size="large" block loading={sending} onClick={requestCode}>
                    Send verification code
                  </Button>
                ) : (
                  <>
                    <Input
                      size="large"
                      autoFocus
                      prefix={<SafetyCertificateOutlined />}
                      placeholder="6-digit code"
                      // 手机上拉起数字键盘；只留数字，避免粘贴带进空格
                      inputMode="numeric"
                      maxLength={6}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    />
                    <Button type="primary" size="large" block loading={verifying} onClick={submitCode}>
                      Verify and continue
                    </Button>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      {/* 没有「换个邮箱」：收件地址由对方绑定，这里改不了 */}
                      <Button
                        type="link"
                        size="small"
                        disabled={cooldown > 0 || sending}
                        onClick={requestCode}
                      >
                        {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                      </Button>
                    </div>
                  </>
                )}
              </Space>
            </>
          )}

          {stage === 'form' && (
            <div ref={formTop}>
              {/* 🔴 到期前先提醒，而不是等他点提交才拦。填一张银行表单要好几分钟，
                  提前说一声他还来得及先交上去。 */}
              {expiringSoon && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                  title="Your verification is about to expire"
                  description="Please submit soon. If it expires, we will ask you for a new code — what you have filled in will be kept."
                />
              )}
              {schemaLoading && !schema ? (
                <div style={{ textAlign: 'center', padding: '48px 0' }}>
                  <Spin />
                </div>
              ) : (
                schema && (
                  <Spin spinning={schemaLoading}>
                    <SchemaForm
                      token={token}
                      collectToken={collectToken}
                      schema={schema}
                      values={values}
                      errors={errors}
                      disabled={submitting}
                      lockedPaths={locked}
                      onChange={onFieldChange}
                    />
                  </Spin>
                )
              )}
            </div>
          )}
        </Card>

        {stage === 'form' && (
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 12 }}>
            The fields above come from the banking network in real time. Choosing a different
            country or transfer method changes what you need to fill in.
          </Text>
        )}
      </div>

      {/* 提交按钮固定在底部：手机上表单很长，不该让人滚到最后才找得到 */}
      {stage === 'form' && schema && (
        <div
          style={{
            position: 'fixed',
            insetInline: 0,
            bottom: 0,
            padding: '12px 16px calc(12px + env(safe-area-inset-bottom))',
            background: '#fff',
            borderTop: '1px solid #f0f0f0',
          }}
        >
          <div style={{ maxWidth: 520, margin: '0 auto' }}>
            <Button type="primary" size="large" block loading={submitting} onClick={doSubmit}>
              Submit payment details
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background: '#f5f5f5',
      }}
    >
      {children}
    </div>
  );
}
